import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';
import { HomeService } from '../home/home.service';
import { MediaService } from '../media/media.service';
import { SharedService } from './shared.service';

export type BackgroundUploadStatus = 'uploading' | 'finalizing' | 'done' | 'error';

export interface BackgroundMediaUploadJob {
  id: string;
  mediaItemId: number;
  fileName: string;
  value: number;
  status: BackgroundUploadStatus;
  exType: 'video' | 'audio';
  errorMessage?: string;
}

export interface StartBackgroundVideoUploadParams {
  mediaItemId: number;
  file: File;
  /** Snapshot of PUT body from editor at upload start (videoId will be set after chunk upload). */
  finalizePayload: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class BackgroundMediaUploadService {
  private static readonly VIDEO_CHUNK_SIZE_BYTES = 25 * 1024 * 1024;

  private jobs: BackgroundMediaUploadJob[] = [];
  private readonly jobsSubject = new BehaviorSubject<BackgroundMediaUploadJob[]>([]);
  readonly jobs$ = this.jobsSubject.asObservable();

  constructor(
    private readonly mediaService: MediaService,
    private readonly homeService: HomeService,
    private readonly sharedService: SharedService,
    private readonly messageService: MessageService,
  ) {}

  getJobs(): BackgroundMediaUploadJob[] {
    return [...this.jobs];
  }

  getJobsForMediaItem(mediaItemId: number): BackgroundMediaUploadJob[] {
    return this.jobs.filter((j) => j.mediaItemId === mediaItemId);
  }

  hasActiveUploads(): boolean {
    return this.jobs.some((j) => j.status === 'uploading' || j.status === 'finalizing');
  }

  /** Legacy shape used by edit-video-item inline progress blocks. */
  toLegacyProgressRows(): Array<{
    value: number;
    fileName: string;
    mediaId: number;
    exType: string;
    status: string;
    totalUploaded?: number;
  }> {
    return this.jobs
      .filter((j) => j.status === 'uploading' || j.status === 'finalizing')
      .map((j) => ({
        value: j.value,
        fileName: j.fileName,
        mediaId: j.mediaItemId,
        exType: j.exType,
        status: j.status === 'finalizing' ? 'uploading' : j.status,
        totalUploaded: 0,
      }));
  }

  startVideoUpload(params: StartBackgroundVideoUploadParams): string {
    const id = this.newJobId();
    const job: BackgroundMediaUploadJob = {
      id,
      mediaItemId: params.mediaItemId,
      fileName: params.file.name,
      value: 1,
      status: 'uploading',
      exType: 'video',
    };
    this.jobs = [...this.jobs, job];
    this.emit();

    const totalChunks = Math.max(1, Math.ceil(params.file.size / BackgroundMediaUploadService.VIDEO_CHUNK_SIZE_BYTES));
    this.uploadVideoChunk(id, params.file, params.mediaItemId, params.finalizePayload, 1, totalChunks, 0, null);
    return id;
  }

  private uploadVideoChunk(
    jobId: string,
    file: File,
    mediaItemId: number,
    finalizePayload: Record<string, unknown>,
    part: number,
    totalChunks: number,
    uploadedBytes: number,
    uploadId: number | null,
  ): void {
    const startOffSet = (part - 1) * BackgroundMediaUploadService.VIDEO_CHUNK_SIZE_BYTES;
    const endOffSet = Math.min(startOffSet + BackgroundMediaUploadService.VIDEO_CHUNK_SIZE_BYTES, file.size);
    const chunk = file.slice(startOffSet, endOffSet);
    const isLastPart = part === totalChunks;

    const formData = new FormData();
    formData.append('file', chunk, file.name);
    formData.append(
      'fileDetails',
      JSON.stringify({
        id: uploadId,
        part,
        startOffSet,
        endOffSet,
        isLastPart,
        name: file.name,
        fileSize: file.size,
        totalChunks,
        uploaded: uploadedBytes,
      }),
    );

    this.mediaService.uploadChunkVideo(formData).subscribe({
      next: (event: HttpEvent<unknown>) => {
        if (event.type === HttpEventType.UploadProgress) {
          const chunkLoaded = Number(event.loaded) || 0;
          const totalUploaded = Math.min(file.size, uploadedBytes + chunkLoaded);
          const progress = Math.round((100 * totalUploaded) / file.size);
          const displayProgress = progress === 100 && !isLastPart ? 99 : progress;
          this.patchJob(jobId, { value: displayProgress, status: 'uploading' });
          return;
        }

        if (!(event instanceof HttpResponse)) {
          return;
        }

        const uploaded = this.extractUploadedMediaData(event);
        const nextUploadId = uploaded.id ?? uploadId;
        if (!isLastPart) {
          this.uploadVideoChunk(
            jobId,
            file,
            mediaItemId,
            finalizePayload,
            part + 1,
            totalChunks,
            endOffSet,
            nextUploadId,
          );
          return;
        }

        this.patchJob(jobId, { value: 100, status: 'finalizing' });
        this.finalizeVideoUpload(jobId, mediaItemId, uploaded.id, finalizePayload);
      },
      error: (error: unknown) => {
        this.sharedService.updateErrorMessage(error);
        const message =
          (error as { error?: { message?: string } })?.error?.message ?? 'Video could not be uploaded.';
        this.patchJob(jobId, { value: 0, status: 'error', errorMessage: message });
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: message,
        });
        this.scheduleRemoveJob(jobId);
      },
    });
  }

  private finalizeVideoUpload(
    jobId: string,
    mediaItemId: number,
    videoId: number | null,
    finalizePayload: Record<string, unknown>,
  ): void {
    if (!videoId) {
      this.patchJob(jobId, { status: 'error', errorMessage: 'Upload succeeded but video id was not returned.' });
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Upload succeeded but video id was not returned by server.',
      });
      this.scheduleRemoveJob(jobId);
      return;
    }

    const payload = { ...finalizePayload, videoId, videoUrl: null };
    this.homeService.updateAdminMediaItem(mediaItemId, payload).subscribe({
      next: () => {
        this.patchJob(jobId, { value: 100, status: 'done' });
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Video Uploaded Successfully',
        });
        this.scheduleRemoveJob(jobId, 4000);
      },
      error: (error: unknown) => {
        const message =
          (error as { error?: { message?: string } })?.error?.message ??
          'Video uploaded but media item update failed.';
        this.patchJob(jobId, { status: 'error', errorMessage: message });
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: message,
        });
        this.scheduleRemoveJob(jobId);
      },
    });
  }

  private patchJob(jobId: string, patch: Partial<BackgroundMediaUploadJob>): void {
    const idx = this.jobs.findIndex((j) => j.id === jobId);
    if (idx < 0) {
      return;
    }
    this.jobs = this.jobs.map((j, i) => (i === idx ? { ...j, ...patch } : j));
    this.emit();
  }

  private scheduleRemoveJob(jobId: string, delayMs = 3000): void {
    setTimeout(() => {
      this.jobs = this.jobs.filter((j) => j.id !== jobId);
      this.emit();
    }, delayMs);
  }

  private emit(): void {
    this.jobsSubject.next([...this.jobs]);
    this.sharedService.updateProgressBarData(this.toLegacyProgressRows());
  }

  private newJobId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  private extractUploadedMediaData(event: HttpResponse<unknown>): { id: number | null } {
    const body = (event?.body ?? {}) as Record<string, unknown>;
    const data = (body?.['data'] ?? body ?? {}) as Record<string, unknown>;
    const idCandidates = [
      data?.['id'],
      data?.['videoId'],
      data?.['VideoId'],
      body?.['id'],
      body?.['videoId'],
    ];
    const resolvedId = idCandidates.find((value) => value !== null && value !== undefined && String(value).trim() !== '');
    const idNum = Number(resolvedId);
    return {
      id: Number.isFinite(idNum) && idNum > 0 ? idNum : null,
    };
  }
}
